/* eslint-disable react/prop-types */
import React, { createContext, useState, useContext, useCallback, useEffect, useMemo } from 'react';

import { useCurrentUser } from '@gsa/afp-shared-ui-utils';

import { createMakeModelApi } from '../../../components/make-model/api-helpers';

const MakeModelPanelContext = createContext();

export function MakeModelPanelProvider({
  allowCustom = true,
  context = 'CMME',
  initialState,
  children,
}) {
  const { getToken } = useCurrentUser();

  const { makesFetchOptionsPromise, modelsFetchOptionsPromise } = useMemo(
    () => createMakeModelApi(context),
    [context],
  );

  const fetchMakes = useCallback(
    async (input, signal) => {
      const token = await getToken();
      return makesFetchOptionsPromise(input, token, signal);
    },
    [getToken],
  );

  const fetchModels = useCallback(
    async (input, makeCode, signal) => {
      const token = await getToken();
      return modelsFetchOptionsPromise(input, makeCode, token, signal);
    },
    [getToken],
  );

  const [bodyMake, setBodyMake] = useState(null);
  const [bodyMakeInputValue, setBodyMakeInputValue] = useState('');
  const [bodyMakeOptions, setBodyMakeOptions] = useState([]);

  const [bodyModel, setBodyModel] = useState(null);
  const [bodyModelInputValue, setBodyModelInputValue] = useState('');
  const [bodyModelOptions, setBodyModelOptions] = useState([]);

  // const [comment, setComment] = useState(null);

  const [dirtyFields, setDirtyFields] = useState([]);

  // Initialize values from initialState
  useEffect(() => {
    if (initialState?.bodyMake) {
      const makeOption = {
        label: initialState.bodyMake.makeName,
        value: initialState.bodyMake.makeCode,
      };
      setBodyMake(makeOption);
      setBodyMakeInputValue(initialState.bodyMake.makeName);
    }

    if (initialState?.bodyModel) {
      const modelOption = {
        label: initialState.bodyModel.modelName,
        value: initialState.bodyModel.modelCode,
      };
      setBodyModel(modelOption);
      setBodyModelInputValue(initialState.bodyModel.modelName);
    }
  }, [initialState]);

  const getErrors = useCallback(() => {
    const requiredMsg = 'This is a required field';
    const errors = [];

    if (!bodyMake) {
      if (allowCustom) {
        if (!bodyMakeInputValue) {
          errors.push({
            field: 'bodyMake',
            message: requiredMsg,
          });
        }
      } else {
        errors.push({
          field: 'bodyMake',
          message: requiredMsg,
        });
      }
    }

    if (!bodyModel) {
      if (allowCustom) {
        if (!bodyModelInputValue) {
          errors.push({
            field: 'bodyModel',
            message: requiredMsg,
          });
        }
      } else {
        errors.push({
          field: 'bodyModel',
          message: requiredMsg,
        });
      }
    }

    // if (!comment) {
    //   errors.push({
    //     field: 'comment',
    //     message: requiredMsg,
    //   });
    // }
    return errors;
  }, [bodyMake, bodyMakeInputValue, bodyModel, bodyModelInputValue, allowCustom]);

  const value = {
    allowCustom,

    bodyMake,
    setBodyMake,
    bodyMakeInputValue,
    setBodyMakeInputValue,
    bodyMakeOptions,
    setBodyMakeOptions,

    bodyModel,
    setBodyModel,
    bodyModelInputValue,
    setBodyModelInputValue,
    bodyModelOptions,
    setBodyModelOptions,

    // comment,
    // setComment,

    fetchMakes,
    fetchModels,

    getErrors,
    dirtyFields,
    setDirtyFields,
  };

  return (
    <MakeModelPanelContext.Provider value={value}>
      {children}
    </MakeModelPanelContext.Provider>
  );
}

export function useMakeModelProvider() {
  const context = useContext(MakeModelPanelContext);
  if (!context) {
    throw new Error(
      'useMakeModelProvider must be used within a MakeModelPanelProvider',
    );
  }
  return context;
}
